import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

export default function PriceChart({ data, ticker }) {
  const isCn = ticker && /\d/.test(ticker);
  const currency = isCn ? '¥' : '$';

  const chartData = useMemo(() => {
    if (!data?.Close) return [];
    const closes = data.Close;
    return Object.entries(closes)
      .map(([dateStr, price]) => ({
        date: new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: Number(price),
        rawDate: new Date(dateStr),
      }))
      .sort((a, b) => a.rawDate - b.rawDate);
  }, [data]);

  if (chartData.length === 0) {
    return <Typography color="text.secondary">No price data to display.</Typography>;
  }

  const first = chartData[0].price;
  const last = chartData[chartData.length - 1].price;
  const isUp = last >= first;
  const color = isUp ? '#00D9A6' : '#FF6B6B';

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2">{ticker} — 6 Month Price</Typography>
          <Typography variant="body2" sx={{ color }}>
            {isUp ? '▲' : '▼'} {((last - first) / first * 100).toFixed(2)}%
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#888' }} interval="preserveStartEnd" />
            <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11, fill: '#888' }}
              tickFormatter={(v) => `${currency}${v.toFixed(0)}`} />
            <Tooltip
              contentStyle={{ background: '#1a1f36', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
              labelStyle={{ color: '#aaa' }}
              formatter={(v) => [`${currency}${Number(v).toFixed(2)}`, 'Price']}
            />
            <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2}
              fill="url(#priceGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
